import { useEffect, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Select,
	Typography,
} from "@mui/material";
import {
	CheckCircle,
	Error as ErrorIcon,
	FiberManualRecord as RecordIcon,
	HourglassEmpty,
	PanTool,
	RadioButtonUnchecked,
	SkipNext,
} from "@mui/icons-material";
import api from "../services/api";
import {
	dataValuesForRow,
	getStepDescription,
	substituteStep,
	substituteText,
} from "../utils/stepUtils";

// Replays the start of a test in a new browser window (via the Testify
// Recorder extension), then records from there — or, in "pick" mode, lets the
// user click the element a step should use. The caller merges the result.
//
// mode         replays          then
// keep/replace steps 0..k       records until Stop (caller keeps / drops k+1..n)
// append       every step       records until Stop
// single       steps 0..k-1     records one action (the extension stops itself)
// pick         steps 0..k-1     reports the clicked element's locators

const TITLES = {
	keep: "Record after this step",
	replace: "Record after this step, replacing the rest",
	append: "Record more steps",
	single: "Re-record this step",
	pick: "Pick the element for this step",
};

const EXTENSION_MODE = { single: "single", pick: "pick" };

const EXTENSION_TIMEOUT_MS = 3000;

const StatusIcon = ({ status }) => {
	switch (status) {
		case "running":
			return <CircularProgress size={18} />;
		case "passed":
			return <CheckCircle color="success" fontSize="small" />;
		case "failed":
			return <ErrorIcon color="error" fontSize="small" />;
		case "skipped":
			return <SkipNext color="disabled" fontSize="small" />;
		case "manual":
			return <PanTool color="info" fontSize="small" />;
		default:
			return <RadioButtonUnchecked color="disabled" fontSize="small" />;
	}
};

export default function ReplayRecordDialog({
	open,
	mode,
	steps,
	anchorIndex,
	startUrl,
	dataBinding,
	onComplete,
	onClose,
}) {
	const replayEnd = mode === "single" || mode === "pick" ? anchorIndex : anchorIndex + 1;
	const prefix = steps.slice(0, replayEnd);
	const canRecordHere = !EXTENSION_MODE[mode];

	// setup → starting → replaying ⇄ paused → recording | picking; ended on abort
	const [phase, setPhase] = useState("setup");
	const [progress, setProgress] = useState({});
	const [pausedAt, setPausedAt] = useState(null);
	const [recorded, setRecorded] = useState([]);
	const [error, setError] = useState("");
	const [dataSet, setDataSet] = useState(null);
	const [rowIndex, setRowIndex] = useState(0);
	const sessionId = useRef(null);
	const fromIndex = useRef(null);
	// When recording (not the replay before it) started
	const recordingStartedAt = useRef(null);
	const acceptTimer = useRef(null);
	// The message listener is registered once per open; read the latest callback
	const onCompleteRef = useRef(onComplete);
	onCompleteRef.current = onComplete;

	const active = !["setup", "ended"].includes(phase);
	const dataSetId = dataBinding?.dataSetId;

	useEffect(() => {
		if (!open) return;
		setPhase("setup");
		setProgress({});
		setPausedAt(null);
		setRecorded([]);
		setError("");
		setRowIndex(0);
		sessionId.current = null;
	}, [open]);

	useEffect(() => {
		if (!open || !dataSetId) return;
		api.get(`/datasets/${dataSetId}`)
			.then(({ data }) => setDataSet(data))
			.catch(() => setError("Couldn't load the test's data set"));
	}, [open, dataSetId]);

	const post = (action, data) =>
		window.postMessage({ action, data }, window.location.origin);

	const finish = (result) => {
		sessionId.current = null;
		onCompleteRef.current(result);
	};

	useEffect(() => {
		if (!open) return undefined;
		const onMessage = (event) => {
			if (event.origin !== window.location.origin) return;
			const { action, data } = event.data || {};
			if (!sessionId.current || data?.sessionId !== sessionId.current) return;

			switch (action) {
				case "REPLAY_ACCEPTED":
					clearTimeout(acceptTimer.current);
					setPhase("replaying");
					break;
				case "REPLAY_REJECTED":
					clearTimeout(acceptTimer.current);
					sessionId.current = null;
					setPhase("setup");
					setError(data.error || "The extension refused to start");
					break;
				case "REPLAY_PROGRESS":
					setProgress((prev) => ({ ...prev, [data.index]: data }));
					if (data.status === "failed") {
						setPausedAt(data.index);
						setPhase("paused");
					} else if (data.status === "running") {
						setPausedAt(null);
						setPhase("replaying");
					}
					break;
				case "RECORDING_STARTED":
					fromIndex.current = data.fromIndex;
					recordingStartedAt.current = Date.now();
					setPausedAt(null);
					setPhase(data.mode === "pick" ? "picking" : "recording");
					break;
				case "STEP_RECORDED":
					setRecorded((prev) => [...prev, data.step]);
					break;
				case "RECORDING_STOPPED":
					if (data.aborted) {
						sessionId.current = null;
						setPhase("ended");
						setError(data.reason || "Replay stopped");
					} else {
						finish({
							recorded: data.steps || [],
							fromIndex: fromIndex.current,
							recordingStartedAt: recordingStartedAt.current,
						});
					}
					break;
				case "ELEMENT_PICKED":
					finish({ picked: data.step });
					break;
				default:
					break;
			}
		};
		window.addEventListener("message", onMessage);
		return () => window.removeEventListener("message", onMessage);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// Leaving the page mid-session must not leave a replay or recording running
	useEffect(
		() => () => {
			clearTimeout(acceptTimer.current);
			if (sessionId.current) post("REQUEST_STOP_RECORDING");
		},
		[],
	);

	const handleStart = () => {
		const values = dataSet
			? dataValuesForRow(dataSet.rows?.[rowIndex], dataSet, dataBinding?.columnMapping)
			: null;
		const replaySteps = values ? prefix.map((s) => substituteStep(s, values)) : prefix;

		sessionId.current = crypto.randomUUID();
		fromIndex.current = null;
		setProgress({});
		setRecorded([]);
		setError("");
		setPhase("starting");
		post("REQUEST_REPLAY_RECORDING", {
			sessionId: sessionId.current,
			steps: replaySteps,
			startUrl: values ? substituteText(startUrl, values) : startUrl,
			mode: EXTENSION_MODE[mode] || "record",
		});
		// The bridge answers at once when the extension is installed
		acceptTimer.current = setTimeout(() => {
			sessionId.current = null;
			setPhase("setup");
			setError(
				"The Testify Recorder extension didn't respond. Make sure it's installed and enabled, then reload this page.",
			);
		}, EXTENSION_TIMEOUT_MS);
	};

	const control = (command) => post("REQUEST_REPLAY_CONTROL", { command });

	const handleStop = () => post("REQUEST_STOP_RECORDING");

	const rangeText =
		prefix.length === 0
			? "Nothing needs replaying"
			: prefix.length === 1
				? "Step 1 is replayed"
				: `Steps 1–${prefix.length} are replayed`;
	const afterText = {
		keep: "then recording starts. New steps go after this step; the steps after it are kept.",
		replace: "then recording starts. New steps go after this step and replace every step after it.",
		append: "then recording starts. New steps are added at the end.",
		single: "then your next action (click, typing, selection) replaces this step.",
		pick: "then click the element this step should use. The click isn't performed.",
	}[mode];

	return (
		<Dialog open={open} maxWidth="sm" fullWidth onClose={active ? undefined : onClose}>
			<DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				{TITLES[mode]}
				{phase === "recording" && (
					<Chip icon={<RecordIcon />} label="Recording…" color="error" size="small" />
				)}
			</DialogTitle>
			<DialogContent dividers>
				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				<Typography variant="body2" sx={{ mb: 2 }}>
					{rangeText} in a new browser window, {afterText}
				</Typography>

				{phase === "setup" && prefix.length > 0 && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						Replay performs these steps for real on the site, just like a test
						run: records get created and forms get sent.
					</Alert>
				)}

				{phase === "setup" && dataSetId && (
					<FormControl fullWidth size="small" sx={{ mb: 2 }}>
						<InputLabel id="replay-data-row-label">Data row for {"{{variables}}"}</InputLabel>
						<Select
							labelId="replay-data-row-label"
							label="Data row for {{variables}}"
							value={dataSet?.rows?.length ? rowIndex : ""}
							onChange={(e) => setRowIndex(e.target.value)}
						>
							{(dataSet?.rows || []).map((row, i) => (
								<MenuItem key={i} value={i}>
									Row {i + 1}:{" "}
									{Object.entries(row)
										.map(([k, v]) => `${k}=${v}`)
										.join(", ")
										.slice(0, 80)}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}

				{prefix.length > 0 && phase !== "setup" && (
					<List dense sx={{ maxHeight: 260, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1, mb: 2 }}>
						{prefix.map((step, i) => (
							<ListItem key={step.id || i} data-testid={`replay-step-${i}`} data-status={progress[i]?.status || "pending"}>
								<ListItemIcon sx={{ minWidth: 32 }}>
									<StatusIcon status={progress[i]?.status} />
								</ListItemIcon>
								<ListItemText
									primary={`${i + 1}. ${getStepDescription(step)}`}
									secondary={progress[i]?.status === "failed" ? progress[i].error : null}
									primaryTypographyProps={{ noWrap: true, variant: "body2" }}
									secondaryTypographyProps={{ color: "error" }}
								/>
							</ListItem>
						))}
					</List>
				)}

				{phase === "paused" && pausedAt !== null && (
					<Alert severity="error" sx={{ mb: 2 }} data-testid="replay-paused">
						<Typography variant="body2" fontWeight={600}>
							Step {pausedAt + 1} couldn't be replayed. Replay is paused.
						</Typography>
						<Typography variant="body2" sx={{ mb: 1 }}>
							{progress[pausedAt]?.error}
						</Typography>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
							<Button size="small" variant="outlined" onClick={() => control("retry")}>
								Retry
							</Button>
							<Button size="small" variant="outlined" onClick={() => control("continue")}>
								I did it manually, continue
							</Button>
							<Button size="small" variant="outlined" onClick={() => control("skip")}>
								Skip
							</Button>
							{canRecordHere && (
								<Button size="small" variant="contained" onClick={() => control("recordHere")}>
									Start recording from here
								</Button>
							)}
							<Button size="small" color="error" onClick={() => control("abort")}>
								Abort
							</Button>
						</Box>
					</Alert>
				)}

				{(phase === "starting" || phase === "replaying") && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
						<HourglassEmpty fontSize="small" />
						<Typography variant="body2">
							{phase === "starting" ? "Opening the recording window…" : "Replaying…"}
						</Typography>
					</Box>
				)}

				{phase === "recording" && (
					<>
						<Alert severity="info" sx={{ mb: 1 }}>
							{mode === "single"
								? "Perform the action in the recording window. Recording stops after it."
								: "Recording in the browser window. Click Stop recording when you're done."}
						</Alert>
						<Typography variant="subtitle2" sx={{ mt: 1 }}>
							New steps ({recorded.length})
						</Typography>
						<List dense data-testid="replay-recorded-steps">
							{recorded.map((step, i) => (
								<ListItem key={i}>
									<ListItemText primary={getStepDescription(step)} primaryTypographyProps={{ variant: "body2", noWrap: true }} />
								</ListItem>
							))}
						</List>
					</>
				)}

				{phase === "picking" && (
					<Alert severity="info">
						Click the element in the recording window. The click is captured,
						not performed.
					</Alert>
				)}
			</DialogContent>
			<DialogActions>
				{phase === "setup" || phase === "ended" ? (
					<>
						<Button onClick={onClose}>Cancel</Button>
						<Button variant="contained" onClick={handleStart} disabled={Boolean(dataSetId) && !dataSet?.rows?.length}>
							{phase === "ended" ? "Try again" : "Start"}
						</Button>
					</>
				) : phase === "recording" ? (
					<Button variant="contained" color="error" onClick={handleStop}>
						Stop recording
					</Button>
				) : (
					<Button color="error" onClick={handleStop} disabled={phase === "starting"}>
						Cancel
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
